import { JobFormPropsWithRates } from '~hooks/useJob/models';
import { LoadOptionsProps } from '~hooks/useLoad/models';

/**
 * Determines which submitted loads need a PATCH request because they differ
 * from the server state. Checks quantity, unit of measure, material, AND
 * waypoint site assignments.
 */
export function getLoadsToUpdate<TLoad extends LoadOptionsProps>(
  submittedLoads: TLoad[],
  serverLoads: LoadOptionsProps[],
): TLoad[] {
  return submittedLoads.filter((submittedLoad) => {
    if (!submittedLoad.id) return false;

    const existingLoad = serverLoads.find((l) => l.id === submittedLoad.id);
    if (!existingLoad) return false;

    const quantityChanged =
      Number(submittedLoad.quantity) !== Number(existingLoad.quantity);

    const unitOfMeasureChanged =
      submittedLoad.unitOfMeasure?.id !== existingLoad.unitOfMeasure?.id;

    const materialChanged =
      (submittedLoad.material?.id ?? null) !== (existingLoad.material?.id ?? null);

    const pickupSiteChanged =
      (submittedLoad.pickUpWaypoints?.[0]?.site?.id ?? null) !==
      (existingLoad.pickUpWaypoints?.[0]?.site?.id ?? null);

    const dropoffSiteChanged =
      (submittedLoad.dropOffWaypoints?.[0]?.site?.id ?? null) !==
      (existingLoad.dropOffWaypoints?.[0]?.site?.id ?? null);

    const stagingSiteChanged =
      (submittedLoad.stagingWaypoints?.[0]?.site?.id ?? null) !==
      (existingLoad.stagingWaypoints?.[0]?.site?.id ?? null);

    return (
      quantityChanged ||
      unitOfMeasureChanged ||
      materialChanged ||
      pickupSiteChanged ||
      dropoffSiteChanged ||
      stagingSiteChanged
    );
  });
}

/**
 * Removes the rate-side edits from the submit payload when the user confirms
 * "Save and re-match". The BE owns the rematched rate and its add-on cascade —
 * sending the FE's rate trio would let `JobService::Update` apply it before
 * `SetMatchingRateOnAssignment` overrides it, and sending the FE's add-on diff
 * would race the BE's `set_auto_add_ons!` cascade (TRE-14733 duplicates,
 * TRE-14734 cascade-overwrite). Condition changes (truck type, material,
 * waypoints) and unrelated edits stay intact.
 *
 * `haulerRateType` is intentionally preserved: the BE rematch does not write
 * `hauler_rate_type`, so there's no race and stripping it would discard the
 * user's intent for that field.
 */
export function stripRateSideEditsForRematch(
  data: JobFormPropsWithRates,
): JobFormPropsWithRates {
  return {
    ...data,
    customerRate: undefined,
    customerRateType: undefined,
    customerRateValue: undefined,
    assigneeRate: undefined,
    siteRateSchedule: undefined,
    customerAddOnAssignments: [],
    vendorAddOnAssignments: [],
    driverAddOnAssignments: [],
  };
}
